<template>
  <div>
    <Popup />
    <!-- EDIT FORM -->
    <v-container>
      <v-form ref="imapForm" v-model="isImapFormValid">
        <v-row>
          <v-col>
            <v-text-field
              v-model="computedCustomerName"
              label="name"
              @input="updateField('customerName')"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="computedCustomerCode"
              label="code"
              @input="updateField('customerCode')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-file-input
              ref="editFormUploadFile"
              accept="image/png, image/jpeg"
              label="logo"
              placeholder="Upload file"
              @change="changedFileUploadOnEditedItem($event, 'logo')"
              prepend-icon="mdi-upload"
            ></v-file-input>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="text--secondary mb-2">logo preview</div>
            <div class="text-center preview-border">
              <div v-if="!computedCustomerLogo">no logo available</div>
              <div v-else>
                <ImageComponent class="logo-preview" :iri="computedCustomerLogo" />
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              v-model="computedCustomerMailFooterNote"
              rows="4"
              label="You can use HTML tags"
              @input="updateField('customerMailFooterNote')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="text--secondary mb-2">notification mail address</div>
            <v-text-field
              v-model="computedCustomerNotificationMailAddress"
              label="notification mail"
              @input="updateField('customerNotificationMailAddress')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="text--secondary mb-2">bcc notification mail addresses</div>
            <v-text-field
              v-model="computedCustomerBccNotificationMailAddresses"
              label="bcc notification mail"
              @input="updateField('customerBccNotificationMailAddresses')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="text--secondary mb-2">Default FO Account ID</div>
            <v-text-field
              v-model="computedCustomerDefaultFoAccountId"
              label="Default FO Account ID"
              @input="updateField('customerDefaultFoAccountId')"
            />
          </v-col>
        </v-row>
        <v-row v-if="isSuperAdmin">
          <v-col>
            <div class="text--secondary mb-2">Support Leading Zeros For FO Product ID</div>
            <v-switch
              v-model="computedSupportLeadingZerosForFoProductId"
              label="Enable leading zeros support"
              @change="updateField('supportLeadingZerosForFoProductId')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h2 class="my-2 font-weight-bold">Ordering hints</h2>
            <v-textarea
              v-model="computedCustomerOrderingHints"
              rows="4"
              label="You can use HTML tags"
              @input="updateField('customerOrderingHints')"
            />
          </v-col>
        </v-row>

        <h2 class="my-2 font-weight-bold">Company Address</h2>
        <v-divider class="mb-4"></v-divider>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="addressName"
              label="Company Name"
              @input="updateField('addressName')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="8">
            <v-text-field
              v-model="addressStreet"
              label="Street"
              @input="updateField('addressStreet')"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="addressStreetNr"
              label="Street Nr."
              @input="updateField('addressStreetNr')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            <v-text-field
              v-model="addressZip"
              label="ZIP Code"
              @input="updateField('addressZip')"
            />
          </v-col>
          <v-col cols="8">
            <v-text-field
              v-model="addressCity"
              label="City"
              @input="updateField('addressCity')"
            />
          </v-col>
        </v-row>

        <div class="text-right">
          <v-btn color="primary mb-2" @click="saveUserSettings" :loading="isSavingUserSettings" :disabled="isSavingUserSettings">Save Customer Settings</v-btn>
        </div>
        <h2 class="my-2 font-weight-bold">Mail Configuration</h2>
        <v-divider class="mb-4"></v-divider>
        <v-row>
          <v-col class="d-flex">
            <v-btn-toggle v-model="mailConfigurationType" mandatory class="my-2">
              <v-btn
                tile
                depressed
                value="imap"
                :color="mailConfigurationType === 'imap' ? 'primary' : 'grey lighten-3'"
                :class="mailConfigurationType === 'imap' ? 'white--text' : 'grey--text text--darken-2'"
              >
                IMAP
              </v-btn>
              <v-btn
                tile
                depressed
                value="oauth"
                :color="mailConfigurationType === 'oauth' ? 'primary' : 'grey lighten-3'"
                :class="mailConfigurationType === 'oauth' ? 'white--text' : 'grey--text text--darken-2'"
              >
                OAuth
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <!-- OAuth Section -->
        <div v-if="mailConfigurationType === 'oauth'">
          <h2 class="my-2 font-weight-bold">Microsoft OAuth Configuration</h2>
          <v-divider class="mb-4"></v-divider>

          <v-row v-if="isLoadingOAuthStatus">
            <v-col>
              <v-card outlined>
                <v-card-text class="text-center">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <div class="mt-4">Checking OAuth configuration...</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-else-if="!oauthStatus.isActive">
            <v-col>
              <v-card outlined>
                <v-card-text>
                  <v-icon size="64" color="grey" class="mb-4">mdi-microsoft</v-icon>
                  <h3 class="mb-4">Microsoft OAuth Not Configured</h3>
                  <div class="mb-4 text--secondary">
                    No active OAuth authorization found for this customer.
                  </div>
                  <v-btn color="primary" large @click="redirectToOAuthAuthorization">
                    <v-icon left>mdi-login</v-icon>
                    Authorize with Microsoft
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-else>
            <v-col>
              <v-card outlined>
                <v-card-title class="success--text">
                  <v-icon left color="success">mdi-check-circle</v-icon>
                  OAuth Authorization Active
                </v-card-title>
                <v-btn color="primary" class='ml-4 mb-4' large @click="redirectToOAuthAuthorization">
                  <v-icon left>mdi-login</v-icon>
                  Re-authorize with Microsoft
                </v-btn>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- IMAP Section -->
        <div v-if="mailConfigurationType === 'imap'">
          <h2 class="my-2 font-weight-bold">IMAP configuration</h2>
          <v-divider class="mb-4"></v-divider>

          <!-- Info banner - test is mandatory before update -->
          <v-alert
            v-if="!isTestedWithSuccess"
            type="warning"
            text
            dense
            class="mb-4">
            You must test your IMAP configuration before updating
          </v-alert>

          <!-- Success banner - shown after successful test -->
          <v-alert
            v-if="isTestedWithSuccess"
            type="success"
            text
            dense
            class="mb-4">
            IMAP configuration test successful! You can now update the configuration.
          </v-alert>

          <v-row>
            <v-col>
              <div class="text--secondary mb-2">IMAP path </div>
              <v-text-field
              v-model="computedCustomerImapConfigurationImapPath"
              label="e.g. 'imap.example.com' or 'mail.clustermail.de:993/imap/ssl/novalidate-cert'"
              @input="updateField('customerImapConfigurationImapPath')"
              :rules="imapPathRules"
              required
            />
            <div class="text--secondary mb-2">user name</div>
            <v-text-field
              v-model="computedCustomerImapConfigurationUserName"
              label="login for IMAP mailbox (usually it is an email)"
              @input="updateField('customerImapConfigurationUserName')"
              :rules="usernameRules"
              required
            />
            <div class="text--secondary mb-2">password</div>
            <v-text-field
              :append-icon="(showPassword && customerImapConfigurationPassword) ? 'mdi-eye' : 'mdi-eye-off'"
              :type="(showPassword && customerImapConfigurationPassword) ? 'text' : 'password'"
              @click:append="showPassword = !showPassword"
              v-model="computedCustomerImapConfigurationPassword"
              label="password for IMAP mailbox"
              @input="updateField('customerImapConfigurationPassword')"
              :rules="hasExistingImapConfig ? [] : passwordRules"
              :required="!hasExistingImapConfig"
            />
          </v-col>
        </v-row>
        </div>
      </v-form>
      <div class="text-right" v-if="mailConfigurationType === 'imap'">
        <v-btn
          class="mr-2"
          color="info"
          @click="testImapConfig"
          :loading="isTestingImap"
          :disabled="!canTestImapConfig">
          Test
        </v-btn>
        <v-btn
          v-if="!hasExistingImapConfig"
          color="primary"
          @click="saveImapConfiguration"
          :loading="isSavingImap"
          :disabled="isSavingImap || !isImapFormValid || !imapConfigChanged.anyChanged">
          Create IMAP Configuration
        </v-btn>
        <v-btn
          v-if="hasExistingImapConfig"
          color="primary"
          @click="saveImapConfiguration"
          :loading="isSavingImap"
          :disabled="!canUpdateImapConfig">
          Update IMAP Configuration
        </v-btn>
      </div>
    </v-container>
    <v-overlay :value="isLoading">
    </v-overlay>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-unresolved, import/extensions
import store from '@/store';
import ImageComponent from '../ImageComponent.vue';
import Popup from '../Popup.vue';

export default {
  name: 'CustomerDetailsSettings',
  components: { Popup, ImageComponent },
  data() {
    return {
      customerName: '',
      customerCode: '',
      customerLogo: '',
      customerMailFooterNote: '',
      customerOrderingHints: '',
      customerNotificationMailAddress: '',
      customerBccNotificationMailAddresses: '',
      customerDefaultFoAccountId: '',
      customerImapConfigurationImapPath: '',
      customerImapConfigurationUserName: '',
      customerImapConfigurationPassword: '',
      customerImapConfigurationId: '',
      supportLeadingZerosForFoProductId: false,
      addressName: '',
      addressStreet: '',
      addressStreetNr: '',
      addressZip: '',
      addressCity: '',
      initialAddress: {
        name: '',
        street: '',
        streetNr: '',
        zip: '',
        city: '',
      },
      initialImapConfig: {
        imapPath: '',
        username: '',
        password: '',
      },
      // OAuth UI state
      mailConfigurationType: 'imap', // 'imap' | 'oauth'
      oauthStatus: {
        isActive: false,
        tokenData: null,
      },
      isLoadingOAuthStatus: false,
      editedItemUploadFiles: [],
      editedItem: {},
      itemBeforeEdit: {},
      showPassword: false,
      isLoading: false,
      isSaving: false,
      isSavingUserSettings: false,
      isSavingImap: false,
      hasExistingImapConfig: false,
      isImapFormValid: false,
      isTestingImap: false,
      hasTestedCurrentConfig: false,
      isTestedWithSuccess: false,
      lastTestedConfig: {
        imapPath: '',
        username: '',
        password: '',
      },
      requiredRules: [
        v => !!v || 'This field is required',
      ],
      emailRules: [
        v => !!v || 'Email address is required',
        v => /.+@.+\..+/.test(v) || 'Please enter a valid email address',
      ],
      usernameRules: [
        v => !!v || 'Username is required',
      ],
      passwordRules: [
        v => !this.hasExistingImapConfig || !!v || v === '' || 'Password is required',
      ],
      imapPathRules: [
        v => !!v || 'IMAP path is required',
        v => /^[\w.-]+\.[\w.-]+(:\d+)?(\/[\w/-]+)?$/.test(v) || 'Please enter a valid IMAP path (e.g. imap.example.com or mail.clustermail.de:993/imap/ssl/novalidate-cert)',
      ],
    };
  },
  computed: {
    imapConfigButtonLabel() {
      return this.hasExistingImapConfig ? 'Update IMAP Configuration' : 'Create IMAP Configuration';
    },
    customerId() {
      return this.$route.params.id;
    },
    imapConfigChanged() {
      if (!this.hasExistingImapConfig) {
        const hasImapPath = !!this.customerImapConfigurationImapPath;
        const hasUsername = !!this.customerImapConfigurationUserName;
        const hasPassword = !!this.customerImapConfigurationPassword;

        return {
          imapPath: hasImapPath,
          username: hasUsername,
          password: hasPassword,
          anyChanged: hasImapPath && hasUsername && hasPassword,
        };
      }

      const imapPathChanged = this.customerImapConfigurationImapPath !== this.initialImapConfig.imapPath;
      const usernameChanged = this.customerImapConfigurationUserName !== this.initialImapConfig.username;
      // Password is considered changed only when user types something (not empty)
      const passwordChanged = this.customerImapConfigurationPassword !== '';

      return {
        imapPath: imapPathChanged,
        username: usernameChanged,
        password: passwordChanged,
        anyChanged: imapPathChanged || usernameChanged || passwordChanged,
      };
    },
    canUpdateImapConfig() {
      // Update button is enabled only after successful test
      return this.isImapFormValid && this.imapConfigChanged.anyChanged && !this.isSavingImap && this.hasTestedCurrentConfig;
    },
    canTestImapConfig() {
      // Test button is enabled only when all three fields are filled (for both new and existing configs)
      // Testing validates connection to EXTERNAL mailbox, so all credentials are required
      const hasImapPath = !!this.customerImapConfigurationImapPath && this.customerImapConfigurationImapPath.trim() !== '';
      const hasUsername = !!this.customerImapConfigurationUserName && this.customerImapConfigurationUserName.trim() !== '';
      const hasPassword = !!this.customerImapConfigurationPassword && this.customerImapConfigurationPassword.trim() !== '';

      return hasImapPath && hasUsername && hasPassword && this.isImapFormValid && !this.isTestingImap;
    },
    computedCustomerName: {
      get() {
        return this.customerName;
      },
      set(value) {
        this.customerName = value;
      },
    },
    computedCustomerCode: {
      get() {
        return this.customerCode;
      },
      set(value) {
        this.customerCode = value;
      },
    },
    computedCustomerLogo: {
      get() {
        return this.customerLogo;
      },
      set(value) {
        this.customerLogo = value;
      },
    },
    computedCustomerMailFooterNote: {
      get() {
        return this.customerMailFooterNote;
      },
      set(value) {
        this.customerMailFooterNote = value;
      },
    },
    computedCustomerOrderingHints: {
      get() {
        return this.customerOrderingHints;
      },
      set(value) {
        this.customerOrderingHints = value;
      },
    },
    computedCustomerNotificationMailAddress: {
      get() {
        return this.customerNotificationMailAddress;
      },
      set(value) {
        this.customerNotificationMailAddress = value;
      },
    },
    computedCustomerDefaultFoAccountId: {
      get() {
        return this.customerDefaultFoAccountId;
      },
      set(value) {
        this.customerDefaultFoAccountId = value;
      },
    },
    computedCustomerBccNotificationMailAddresses: {
      get() {
        return this.customerBccNotificationMailAddresses;
      },
      set(value) {
        this.customerBccNotificationMailAddresses = value;
      },
    },
    computedCustomerImapConfigurationImapPath: {
      get() {
        return this.customerImapConfigurationImapPath;
      },
      set(value) {
        this.customerImapConfigurationImapPath = value;
      },
    },
    computedCustomerImapConfigurationUserName: {
      get() {
        return this.customerImapConfigurationUserName;
      },
      set(value) {
        this.customerImapConfigurationUserName = value;
      },
    },
    computedCustomerImapConfigurationPassword: {
      get() {
        return this.customerImapConfigurationPassword;
      },
      set(value) {
        this.customerImapConfigurationPassword = value;
      },
    },
    computedSupportLeadingZerosForFoProductId: {
      get() {
        return this.supportLeadingZerosForFoProductId;
      },
      set(value) {
        this.supportLeadingZerosForFoProductId = value;
      },
    },
    isSuperAdmin() {
      return this.$store.getters['auth/isSuperuser'];
    },

  },
  methods: {
    async testImapConfig() {
      try {
        this.isTestingImap = true;
        const baseUrl = process.env.VUE_APP_BASE_URL;
        const testEndpoint = '/api/imap_configurations/test';
        const testPayload = {
          imapPath: this.customerImapConfigurationImapPath,
          username: this.customerImapConfigurationUserName,
          password: this.customerImapConfigurationPassword,
        };
        const response = await this.$http.post(`${baseUrl}${testEndpoint}`, testPayload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.status === 200) {
          const responseData = response.data;
          if (responseData.status === 'fail') {
            this.showAlertPopup(`IMAP configuration test failed: ${responseData.message}`, 'error');
            this.hasTestedCurrentConfig = false;
            this.isTestedWithSuccess = false;
          } else {
            this.showAlertPopup('IMAP configuration test successful! You can now update the configuration.', 'success');
            // Mark as tested and store the tested config
            this.hasTestedCurrentConfig = true;
            this.isTestedWithSuccess = true;
            this.lastTestedConfig = {
              imapPath: this.customerImapConfigurationImapPath,
              username: this.customerImapConfigurationUserName,
              password: this.customerImapConfigurationPassword,
            };
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('IMAP configuration test failed:', error);
        this.showAlertPopup(`IMAP configuration test failed: ${error.response?.data?.detail || error.message || 'Unknown error'}`, 'error');
        this.hasTestedCurrentConfig = false;
        this.isTestedWithSuccess = false;
      } finally {
        this.isTestingImap = false;
      }
    },

    async getCustomerObject() {
      this.isLoading = true;
      try {
        const baseUrl = process.env.VUE_APP_BASE_URL;
        const response = await this.$http.get(`${baseUrl}/api/customers/${this.customerId}`);

        this.itemBeforeEdit = { ...response.data };
        this.editedItem = { ...response.data };

        this.customerName = this.editedItem.name;
        this.customerCode = this.editedItem.code;
        this.customerMailFooterNote = this.editedItem.mailFooterNote ?? '';
        this.customerOrderingHints = this.editedItem.orderingHints ?? '';
        this.customerNotificationMailAddress = this.editedItem.notificationMail ?? '';
        this.customerBccNotificationMailAddresses = this.editedItem.bccNotificationMail?.join(',') ?? '';
        this.customerDefaultFoAccountId = this.editedItem.defaultFoAccountId ?? '';
        this.customerLogo = this.editedItem.logo;
        if (this.isSuperAdmin) {
          this.supportLeadingZerosForFoProductId = this.editedItem.supportLeadingZerosForFoProductId ?? false;
        }

        const address = this.editedItem.address || {};
        this.addressName = address.name ?? '';
        this.addressStreet = address.street ?? '';
        this.addressStreetNr = address.streetNr ?? '';
        this.addressZip = address.zip ?? '';
        this.addressCity = address.city ?? '';
        this.initialAddress = {
          name: this.addressName,
          street: this.addressStreet,
          streetNr: this.addressStreetNr,
          zip: this.addressZip,
          city: this.addressCity,
        };

        this.$store.commit('setCustomPageTitle', `Settings for ${this.editedItem.name}`);

        // Check if IMAP configuration exists before trying to fetch it
        let imapConfig = null;
        if (response.data.imapConfiguration && response.data.imapConfiguration !== '') {
          try {
            const imapResponse = await this.$http.get(`${baseUrl}${response.data.imapConfiguration}`);
            imapConfig = imapResponse.data;
          } catch (imapError) {
            // eslint-disable-next-line no-console
            console.error('Error while fetching IMAP configuration:', imapError);
          }
        }


        if (imapConfig) {
          this.hasExistingImapConfig = true;
          this.customerImapConfigurationImapPath = imapConfig.imapPath ?? '';
          this.customerImapConfigurationUserName = imapConfig.username ?? '';
          this.customerImapConfigurationPassword = '';
          this.customerImapConfigurationId = imapConfig.id;

          // Store initial values for comparison
          this.initialImapConfig = {
            imapPath: imapConfig.imapPath ?? '',
            username: imapConfig.username ?? '',
            password: '',
          };

          this.editedItem.imapConfiguration = { ...imapConfig };
          this.itemBeforeEdit.imapConfiguration = { ...imapConfig };
        } else {
          this.hasExistingImapConfig = false;
          this.customerImapConfigurationImapPath = '';
          this.customerImapConfigurationUserName = '';
          this.customerImapConfigurationPassword = '';

          // Reset initial values
          this.initialImapConfig = {
            imapPath: '',
            username: '',
            password: '',
          };

          this.editedItem.imapConfiguration = null;
          this.itemBeforeEdit.imapConfiguration = null;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error while fetching customer or IMAP data:', e);
        this.showAlertPopup('Error while fetching customer data', 'error');
        // Reset IMAP fields on error as well
        this.hasExistingImapConfig = false;
        this.customerImapConfigurationImapPath = '';
        this.customerImapConfigurationUserName = '';
        this.customerImapConfigurationPassword = '';
        this.editedItem.imapConfiguration = null;
        this.itemBeforeEdit.imapConfiguration = null;
      } finally {
        this.isLoading = false;
      }
    },
    showAlertPopup(message, status) {
      store.state.popupAlertMessage = message;
      store.state.popupAlertType = status;
      store.state.popupAlertStatus = true;
    },
    changedFileUploadOnEditedItem(file, key) {
      this.editedItemUploadFiles.push({ key, file });
    },
    async redirectToOAuthAuthorization() {
      try {
        const authorizeEndpoint = process.env.VUE_APP_OAUTH_MS_AUTHORIZE_URL;
        const url = `${authorizeEndpoint}?customerCode=${encodeURIComponent(this.customerCode)}`;
        window.location.assign(url);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('OAuth authorize redirect failed', e);
        this.showAlertPopup('Authorization redirect failed', 'error');
      }
    },
    async checkOAuthStatus() {
      try {
        this.isLoadingOAuthStatus = true;
        const baseUrl = process.env.VUE_APP_BASE_URL;
        const response = await this.$http.get(`${baseUrl}/api/oauth_tokens`, {
          params: {
            'customer.id': this.customerId,
            isActive: true,
          },
        });
        const list = (response && response.data && (response.data['hydra:member'] || response.data)) || [];
        if (Array.isArray(list) && list.length > 0) {
          // eslint-disable-next-line prefer-destructuring
          this.oauthStatus.tokenData = list[0];
          this.oauthStatus.isActive = true;
        } else {
          this.oauthStatus.isActive = false;
          this.oauthStatus.tokenData = null;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking OAuth status:', error);
        this.oauthStatus.isActive = false;
        this.oauthStatus.tokenData = null;
      } finally {
        this.isLoadingOAuthStatus = false;
      }
    },
    checkAuthorizationResult() {
      const q = (this.$route && this.$route.query) ? this.$route.query : {};
      const success = q.oauth_success === 'true' || q.auth_success === 'true';
      const error = q.oauth_error === 'true' || q.auth_error === 'true';
      if (success) {
        this.showAlertPopup('OAuth authorization completed successfully!', 'success');
        this.checkOAuthStatus();
      }
      if (error) {
        this.showAlertPopup('OAuth authorization failed. Please try again.', 'error');
      }
      if (success || error) {
        this.$router.replace({ path: this.$route.path });
      }
    },
    updateField(fieldName) {
      switch (fieldName) {
        case 'customerName':
          this.computedCustomerName = this[fieldName];
          this.editedItem.name = this.computedCustomerName;
          break;
        case 'customerCode':
          this.computedCustomerCode = this[fieldName];
          this.editedItem.code = this.computedCustomerCode;
          break;
        case 'customerMailFooterNote':
          this.computedCustomerMailFooterNote = this[fieldName];
          this.editedItem.mailFooterNote = this.computedCustomerMailFooterNote;
          break;
        case 'customerNotificationMailAddress':
          this.computedCustomerNotificationMailAddress = this[fieldName];
          this.editedItem.notificationMail = this.computedCustomerNotificationMailAddress;
          break;
        case 'customerDefaultFoAccountId':
          this.computedCustomerDefaultFoAccountId = this[fieldName];
          this.editedItem.defaultFoAccountId = this.computedCustomerDefaultFoAccountId;
          break;
        case 'customerBccNotificationMailAddresses':
          this.computedCustomerBccNotificationMailAddresses = this[fieldName];
          this.editedItem.bccNotificationMail = this[fieldName];
          break;
        case 'customerOrderingHints':
          this.computedCustomerOrderingHints = this[fieldName];
          this.editedItem.orderingHints = this.computedCustomerOrderingHints;
          break;
        case 'customerImapConfigurationImapPath':
        case 'customerImapConfigurationUserName':
        case 'customerImapConfigurationPassword':
          this.updateImapConfiguration(fieldName);
          // Reset test state when any IMAP field changes
          this.hasTestedCurrentConfig = false;
          this.isTestedWithSuccess = false;
          break;
        case 'supportLeadingZerosForFoProductId':
          if (this.isSuperAdmin) {
            this.computedSupportLeadingZerosForFoProductId = this[fieldName];
            this.editedItem.supportLeadingZerosForFoProductId = this.computedSupportLeadingZerosForFoProductId;
          }
          break;
        case 'addressName':
        case 'addressStreet':
        case 'addressStreetNr':
        case 'addressZip':
        case 'addressCity':
          break;
        default:
          // Handle unknown field name
          this.$emit('field-error', `Unknown field: ${fieldName}`);
          break;
      }
    },
    updateImapConfiguration(fieldName) {
      if (!this.editedItem.imapConfiguration) {
        this.editedItem.imapConfiguration = {};
      }
      const fieldMapping = {
        customerImapConfigurationImapPath: 'imapPath',
        customerImapConfigurationUserName: 'username',
        customerImapConfigurationPassword: 'password',
      };
      const propertyName = fieldMapping[fieldName];
      if (propertyName) {
        this.editedItem.imapConfiguration[propertyName] = this[fieldName];
      }
    },
    async save() {
      this.isSaving = true;
      try {
        await this.updateObject();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('An error occurred while saving:', error);
        this.showAlertPopup('Error while saving', 'error');
      } finally {
        this.isSaving = false;
      }
    },
    async saveUserSettings() {
      this.isSavingUserSettings = true;
      try {
        await this.updateUserSettings();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('An error occurred while saving user settings:', error);
        this.showAlertPopup('Error while saving user settings', 'error');
      } finally {
        this.isSavingUserSettings = false;
      }
    },
    async saveImapConfiguration() {
      if (!this.isImapFormValid) {
        this.showAlertPopup('Please fix the errors in the form before submitting', 'error');
        return;
      }

      if (!this.imapConfigChanged.anyChanged) {
        this.showAlertPopup('No changes detected', 'info');
        return;
      }

      this.isSavingImap = true;
      try {
        await this.updateImapConfigurationSettings();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('An error occurred while saving IMAP configuration:', error);
        this.showAlertPopup(`Error ${this.hasExistingImapConfig ? 'updating' : 'creating'} IMAP configuration`, 'error');
      } finally {
        this.isSavingImap = false;
      }
    },
    async updateUserSettings() {
      try {
        if (this.editedItemUploadFiles.length > 0) {
          await this.uploadFiles((fileObjKey, response) => {
            this.editedItem[fileObjKey] = `/api/media_objects/${response.data.id}`;
          });
        }

        if (typeof this.editedItem.bccNotificationMail === 'string' && this.editedItem.bccNotificationMail !== '') {
          this.editedItem.bccNotificationMail = this.editedItem.bccNotificationMail.split(',').map(email => email.trim());
        }

        if (this.editedItem.bccNotificationMail === '') {
          this.editedItem.bccNotificationMail = null;
        }

        if (this.editedItem.notificationMail === '') {
          this.editedItem.notificationMail = null;
        }

        if (this.editedItem.defaultFoAccountId === '') {
          this.editedItem.defaultFoAccountId = null;
        }

        if (this.editedItem.mailFooterNote === '') {
          this.editedItem.mailFooterNote = null;
        }

        if (this.editedItem.orderingHints === '') {
          this.editedItem.orderingHints = null;
        }

        const payload = { ...this.editedItem };
        delete payload.id;
        delete payload.imapConfiguration;
        delete payload.address;

        // Remove supportLeadingZerosForFoProductId if user is not super admin
        if (!this.isSuperAdmin) {
          delete payload.supportLeadingZerosForFoProductId;
        }

        const payloadKeys = Object.keys(payload);
        for (let i = 0; i < payloadKeys.length; i++) {
          if (JSON.stringify(payload[payloadKeys[i]]) === JSON.stringify(this.itemBeforeEdit[payloadKeys[i]])) {
            delete payload[payloadKeys[i]];
          }
        }

        const addressPayload = {};
        let hasAddressChanges = false;
        const addressFields = {
          name: this.addressName,
          street: this.addressStreet,
          streetNr: this.addressStreetNr,
          zip: this.addressZip,
          city: this.addressCity,
        };
        Object.keys(addressFields).forEach((key) => {
          if (addressFields[key] !== this.initialAddress[key]) {
            addressPayload[key] = addressFields[key] || null;
            hasAddressChanges = true;
          }
        });
        if (hasAddressChanges) {
          payload.address = addressPayload;
        }

        const baseUrl = process.env.VUE_APP_BASE_URL;
        const endpoint = '/api/customers/';

        const response = await this.$http.patch(`${baseUrl}${endpoint}${this.editedItem.id}`, payload, {
          headers: {
            'Content-Type': 'application/merge-patch+json',
          },
        });
        const statusCode = response.status;
        if (statusCode === 200) {
          await this.getCustomerObject();
          this.showAlertPopup('User settings - saved', 'info');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('An error occurred while updating user settings:', error);
      }
    },
    async updateImapConfigurationSettings() {
      try {
        const baseUrl = process.env.VUE_APP_BASE_URL;
        let response;
        let statusCode;


        // Additional validation for username and password
        if (this.customerImapConfigurationUserName.trim() === '') {
          this.showAlertPopup('Username cannot be empty', 'error');
          return;
        }

        // For new configs, password is required. For existing configs, empty password means "don't change"
        if (!this.hasExistingImapConfig && this.customerImapConfigurationPassword.trim() === '') {
          this.showAlertPopup('Password cannot be empty', 'error');
          return;
        }

        if (this.hasExistingImapConfig) {
          // Create payload with only changed fields
          const patchPayload = {};

          if (this.imapConfigChanged.imapPath) {
            patchPayload.imapPath = this.customerImapConfigurationImapPath || '';
          }

          if (this.imapConfigChanged.username) {
            // Additional check to prevent sending empty username
            if (this.customerImapConfigurationUserName.trim() === '') {
              this.showAlertPopup('Username cannot be empty', 'error');
              return;
            }
            patchPayload.username = this.customerImapConfigurationUserName;
          }

          if (this.imapConfigChanged.password) {
            // Only send password if user entered a new value (not empty)
            if (this.customerImapConfigurationPassword.trim() !== '') {
              patchPayload.password = this.customerImapConfigurationPassword;
            }
          }

          // Check if any fields were changed
          if (Object.keys(patchPayload).length === 0) {
            this.showAlertPopup('No changes to save in IMAP configuration', 'info');
            return;
          }

          const patchEndpoint = '/api/imap_configurations/';

          response = await this.$http.patch(`${baseUrl}${patchEndpoint}${this.customerImapConfigurationId}`, patchPayload, {
            headers: {
              'Content-Type': 'application/merge-patch+json',
            },
          });
          statusCode = response.status;
          if (statusCode === 200) {
            // Update initialImapConfig after successful save
            if (patchPayload.imapPath !== undefined) {
              this.initialImapConfig.imapPath = patchPayload.imapPath;
            }

            if (patchPayload.username !== undefined) {
              this.initialImapConfig.username = patchPayload.username;
            }

            if (patchPayload.password !== undefined) {
              this.initialImapConfig.password = this.customerImapConfigurationPassword;
            }

            await this.getCustomerObject();
            this.showAlertPopup('IMAP configuration updated successfully', 'info');
          }
        } else {
          // Create new IMAP configuration

          // Additional validation for new configuration
          if (this.customerImapConfigurationImapPath.trim() === '') {
            this.showAlertPopup('IMAP path cannot be empty', 'error');
            return;
          }

          if (this.customerImapConfigurationUserName.trim() === '') {
            this.showAlertPopup('Username cannot be empty', 'error');
            return;
          }

          if (this.customerImapConfigurationPassword.trim() === '') {
            this.showAlertPopup('Password cannot be empty', 'error');
            return;
          }

          const postPayload = {
            imapPath: this.customerImapConfigurationImapPath,
            username: this.customerImapConfigurationUserName,
            password: this.customerImapConfigurationPassword,
            customer: `/api/customers/${this.customerId}`,
          };

          const postEndpoint = '/api/imap_configurations';

          response = await this.$http.post(`${baseUrl}${postEndpoint}`, postPayload, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          statusCode = response.status;
          if (statusCode === 201) { // 201 Created
            this.hasExistingImapConfig = true;
            this.initialImapConfig.imapPath = this.customerImapConfigurationImapPath || '';
            this.initialImapConfig.username = this.customerImapConfigurationUserName || '';
            this.initialImapConfig.password = this.customerImapConfigurationPassword;

            await this.getCustomerObject();
            this.showAlertPopup('IMAP configuration created successfully', 'info');
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('An error occurred with IMAP configuration:', error);
        this.showAlertPopup(`Error ${this.hasExistingImapConfig ? 'updating' : 'creating'} IMAP configuration`, 'error');
        throw error;
      }
    },
    async updateObject() {
      try {
        if (this.editedItemUploadFiles.length > 0) {
          await this.uploadFiles((fileObjKey, response) => {
            this.editedItem[fileObjKey] = `/api/media_objects/${response.data.id}`;
          });
        }
        if (typeof this.editedItem.bccNotificationMail === 'string' && this.editedItem.bccNotificationMail !== '') { this.editedItem.bccNotificationMail = this.editedItem.bccNotificationMail.split(',').map(email => email.trim()); }

        if (this.editedItem.bccNotificationMail === '') {
          this.editedItem.bccNotificationMail = null;
        }

        if (this.editedItem.notificationMail === '') {
          this.editedItem.notificationMail = null;
        }

        if (this.editedItem.defaultFoAccountId === '') {
          this.editedItem.defaultFoAccountId = null;
        }

        if (this.editedItem.mailFooterNote === '') {
          this.editedItem.mailFooterNote = null;
        }

        if (this.editedItem.orderingHints === '') {
          this.editedItem.orderingHints = null;
        }

        if (!this.editedItem.imapConfiguration) {
          this.editedItem.imapConfiguration = {};
        }
        this.editedItem.imapConfiguration.imapPath = this.editedItem.imapConfiguration.imapPath ?? '';
        this.editedItem.imapConfiguration.username = this.editedItem.imapConfiguration.username ?? '';
        this.editedItem.imapConfiguration.password = this.editedItem.imapConfiguration.password ?? '';

        const payload = { ...this.editedItem };
        delete payload.id;
        const payloadKeys = Object.keys(payload);
        for (let i = 0; i < payloadKeys.length; i++) {
          if (JSON.stringify(payload[payloadKeys[i]]) === JSON.stringify(this.itemBeforeEdit[payloadKeys[i]])) {
            delete payload[payloadKeys[i]];
          }
        }
        const baseUrl = process.env.VUE_APP_BASE_URL;
        const endpoint = '/api/customers/';

        const response = await this.$http.patch(`${baseUrl}${endpoint}${this.editedItem.id}`, payload, {
          headers: {
            'Content-Type': 'application/merge-patch+json',
          },
        });
        const statusCode = response.status;
        if (statusCode === 200) {
          await this.getCustomerObject();
          this.showAlertPopup('Customer settings - saved', 'info');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('An error occurred while updating customer data:', error);
        this.showAlertPopup('Error while updating customer data', 'error');
        throw error;
      }
    },
    uploadFiles(callback) {
      return new Promise((resolve, reject) => {
        const promises = [];

        // process files for contect object
        this.editedItemUploadFiles.forEach((fileObj) => {
          const formData = new FormData();
          formData.append('file', fileObj.file);

          promises.push(
            this.$http.post('api/media_objects', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }).then((res) => {
              callback(fileObj.key, res);
            }).catch((error) => {
              // eslint-disable-next-line no-console
              console.error('Error uploading file:', error);
              this.showAlertPopup(`Error uploading file: ${fileObj.file.name}`, 'error');
              throw error;
            }),
          );
        });

        Promise.all(promises).then(() => {
          this.editedItemUploadFiles = []; // clear array only if we really send file
          resolve();
        }).catch((error) => {
          reject(error);
        });
      });
    },
  },
  mounted() {
    this.$store.state.showCdLinks = true;
  },
  async created() {
    await this.getCustomerObject();
    // determine default mail config type
    const savedType = localStorage.getItem(`mailConfigType_${this.customerId}`);
    await this.checkOAuthStatus();
    if (savedType === 'imap' || savedType === 'oauth') {
      this.mailConfigurationType = savedType;
    } else {
      // if tokens active, default to oauth, else imap
      this.mailConfigurationType = this.oauthStatus.isActive ? 'oauth' : 'imap';
    }
    // handle auth callback result from query
    this.checkAuthorizationResult();
  },
  destroyed() {
    this.$store.state.showCdLinks = false;
    this.$store.commit('clearCustomPageTitle');
  },
};
</script>

<style scoped>
.logo-preview {
  margin: 10px;
  max-height: 120px;
}
.preview-border {
  border-style: solid;
  border-width: 1px;
  border-color: #ccc;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
